import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import UploadProgress from '@/components/UploadProgress';
import ManuscriptOutlinePreview from '@/components/ManuscriptOutlinePreview';
import { useNotification } from '@/contexts/NotificationContext';
import { GENRE_OPTIONS, getThemesForGenre } from '../constants/genreThemes';
import {
  Upload,
  BookOpen,
  User,
  Building2,
  FileText,
  Loader2,
  Trash2,
  Archive,
  MoreVertical,
  Image as ImageIcon,
  ExternalLink,
  ShoppingCart,
  Edit,
  List,
  Play,
  GripVertical
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { BACKEND_URL } from '@/config/api';

// BACKEND_URL imported from centralized config
const API = `${BACKEND_URL}/api`;

// Sortable Chapter Item Component for Drag-and-Drop
const SortableChapterItem = ({ chapter, index, onRefine, onDelete, onTypeChange, loadingSplits, refineSectionIndex, savingOutline }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: chapter.id || `chapter-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Segment type badge colors
  const typeColors = {
    'front_matter': 'bg-blue-100 text-blue-700',
    'chapter': 'bg-gray-100 text-gray-700',
    'part': 'bg-purple-100 text-purple-700',
    'back_matter': 'bg-amber-100 text-amber-700',
    'appendix': 'bg-orange-100 text-orange-700',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition-colors ${
        isDragging ? 'shadow-lg' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1">
          {/* Drag Handle */}
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing mt-1 text-gray-400 hover:text-gray-600"
          >
            <GripVertical className="w-5 h-5" />
          </div>
          
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <span className="text-sm font-bold text-gray-500">#{index}</span>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <div className="font-bold text-gray-900">{chapter.label}</div>
                  {/* Segment Type Badge/Selector */}
                  <select
                    value={chapter.type || 'chapter'}
                    onChange={(e) => onTypeChange && onTypeChange(index, e.target.value)}
                    className={`text-xs px-2 py-0.5 rounded-full border-0 cursor-pointer ${typeColors[chapter.type] || typeColors['chapter']}`}
                    disabled={savingOutline}
                  >
                    <option value="front_matter">Front Matter</option>
                    <option value="part">Part</option>
                    <option value="chapter">Chapter</option>
                    <option value="appendix">Appendix</option>
                    <option value="back_matter">Back Matter</option>
                  </select>
                </div>
                {chapter.title && (
                  <div className="text-sm text-gray-600 mt-1">{chapter.title}</div>
                )}
                <div className="text-xs text-gray-400 mt-1">
                  {chapter.start_char_index} - {chapter.end_char_index} 
                  ({(chapter.end_char_index - chapter.start_char_index).toLocaleString()} chars)
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="border-purple-300 hover:bg-purple-50"
            onClick={() => onRefine(index)}
            disabled={loadingSplits && refineSectionIndex === index}
          >
            {loadingSplits && refineSectionIndex === index ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <List className="w-4 h-4 mr-2" />
                Refine
              </>
            )}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-red-300 hover:bg-red-50 text-red-600"
            onClick={() => onDelete(index)}
            disabled={savingOutline}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { notifyError, notifyWarning, notifyInfo, notifySilentLog } = useNotification();
  const [manuscripts, setManuscripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  
  // Delete/Archive dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  
  // Outline preview state
  const [showOutlinePreview, setShowOutlinePreview] = useState(false);
  const [outlineManuscriptId, setOutlineManuscriptId] = useState(null);
  const [selectedManuscript, setSelectedManuscript] = useState(null);
  
  // Edit state
  const [showEdit, setShowEdit] = useState(false);
  const [editingManuscript, setEditingManuscript] = useState(null);
  
  // Upload progress state
  const [uploadProgress, setUploadProgress] = useState(null);
  const [pendingManuscript, setPendingManuscript] = useState(null); // Holds manuscript_id and session_id for confirmation
  
  // Upload form state
  const [file, setFile] = useState(null);
  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [author, setAuthor] = useState('');
  const [publisher, setPublisher] = useState('');
  const [genre, setGenre] = useState(undefined);
  const [theme, setTheme] = useState(undefined);
  const [purchaseUrl, setPurchaseUrl] = useState('');

  // Outline Editor state
  const [showOutlineEditor, setShowOutlineEditor] = useState(false);
  const [outlineEditorManuscript, setOutlineEditorManuscript] = useState(null);
  const [outlineChapters, setOutlineChapters] = useState([]);
  const [loadingOutline, setLoadingOutline] = useState(false);
  const [savingOutline, setSavingOutline] = useState(false);
  const [isFirstTimeReview, setIsFirstTimeReview] = useState(false); // Track if this is pre-TTS review
  const [showOutlineWarning, setShowOutlineWarning] = useState(false); // Warning dialog
  const [showCancelIngestionDialog, setShowCancelIngestionDialog] = useState(false); // Cancel ingestion confirmation
  const [cancellingIngestion, setCancellingIngestion] = useState(false); // Cancel in progress
  
  // Refine Section state
  const [showRefineSplits, setShowRefineSplits] = useState(false);
  const [refineSectionIndex, setRefineSectionIndex] = useState(null);
  const [proposedSplits, setProposedSplits] = useState([]);
  const [loadingSplits, setLoadingSplits] = useState(false);
  const [originalSectionInfo, setOriginalSectionInfo] = useState(null);

  // Drag-and-drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadManuscripts();
  }, []);

  // Handle outline query parameter from Creator Console
  useEffect(() => {
    const outlineId = searchParams.get('outline');
    if (outlineId && manuscripts.length > 0 && !showOutlineEditor) {
      const manuscript = manuscripts.find(m => m.id === outlineId);
      if (manuscript) {
        openOutlineEditor(manuscript, null, false);
        // Clear the query param but preserve state
        navigate('/app', { replace: true, state: location.state });
      }
    }
  }, [searchParams, manuscripts, showOutlineEditor]);

  // Handle upload query parameter from Creator Console
  useEffect(() => {
    const shouldShowUpload = searchParams.get('upload');
    if (shouldShowUpload === 'true' && !showUpload && !showEdit) {
      setShowUpload(true);
      // Clear the query param but preserve state
      navigate('/app', { replace: true, state: location.state });
    }
  }, [searchParams, showUpload, showEdit]);

  // Auto-refresh manuscripts if any are processing
  useEffect(() => {
    const hasProcessing = manuscripts.some(m => m.tts_generation_status === 'processing');
    
    if (hasProcessing) {
      const interval = setInterval(() => {
        loadManuscripts();
      }, 3000); // Poll every 3 seconds
      
      return () => clearInterval(interval);
    }
  }, [manuscripts]);

  const loadManuscripts = async () => {
    try {
      const response = await axios.get(`${API}/manuscripts?include_archived=${showArchived}`);
      setManuscripts(response.data);
    } catch (error) {
      console.error('Error loading manuscripts:', error);
      notifyError('Failed to load library');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteManuscript = async () => {
    if (!selectedManuscript) return;
    
    try {
      await axios.delete(`${API}/manuscripts/${selectedManuscript.id}`);
      // UI updates via loadManuscripts - no toast needed
      setDeleteDialogOpen(false);
      setSelectedManuscript(null);
      await loadManuscripts();
    } catch (error) {
      console.error('Error deleting manuscript:', error);
      notifyError('Failed to delete manuscript');
    }
  };

  const handleArchiveManuscript = async () => {
    if (!selectedManuscript) return;
    
    try {
      const currentStatus = selectedManuscript.status || 'new';
      const newStatus = currentStatus === 'archived' ? 'active' : 'archived';
      
      await axios.patch(`${API}/manuscripts/${selectedManuscript.id}/status`, null, {
        params: { status: newStatus }
      });
      
      // UI updates via loadManuscripts - no toast needed
      setArchiveDialogOpen(false);
      setSelectedManuscript(null);
      await loadManuscripts();
    } catch (error) {
      console.error('Error updating manuscript status:', error);
      notifyError('Failed to update manuscript status');
    }
  };

  const handleActivateManuscript = async (manuscript, e) => {
    e.stopPropagation();
    
    try {
      await axios.patch(`${API}/manuscripts/${manuscript.id}/status`, null, {
        params: { status: 'active' }
      });
      
      // UI updates via loadManuscripts - no toast needed
      await loadManuscripts();
    } catch (error) {
      console.error('Error activating manuscript:', error);
      notifyError('Failed to activate manuscript');
    }
  };

  const openDeleteDialog = (manuscript, e) => {
    e.stopPropagation();
    setSelectedManuscript(manuscript);
    setDeleteDialogOpen(true);
  };

  const openArchiveDialog = (manuscript, e) => {
    e.stopPropagation();
    setSelectedManuscript(manuscript);
    setArchiveDialogOpen(true);
  };

  const openEditDialog = (manuscript, e) => {
    e.stopPropagation();
    setEditingManuscript(manuscript);
    setTitle(manuscript.title || '');
    setSubtitle(manuscript.subtitle || '');
    setAuthor(manuscript.author || '');
    setPublisher(manuscript.publisher || '');
    // Use undefined instead of empty string for Select components to avoid crashes
    setGenre(manuscript.genre || undefined);
    setTheme(manuscript.theme || undefined);
    setPurchaseUrl(manuscript.purchase_url || '');
    setCoverImage(null);
    setCoverPreview(null);
    setShowEdit(true);
  };

  const openOutlineEditor = async (manuscript, e, isFirstTime = false) => {
    if (e) e.stopPropagation();
    
    // Show warning dialog before opening editor
    if (isFirstTime || manuscript.tts_generation_status === 'pending') {
      setOutlineEditorManuscript(manuscript);
      setIsFirstTimeReview(isFirstTime);
      setShowOutlineWarning(true);
      return;
    }
    
    // Load outline directly if not first time
    await loadOutlineForEditing(manuscript, isFirstTime);
  };

  const loadOutlineForEditing = async (manuscript, isFirstTime = false) => {
    setOutlineEditorManuscript(manuscript);
    setIsFirstTimeReview(isFirstTime);
    setLoadingOutline(true);
    setShowOutlineEditor(true);
    
    try {
      const response = await axios.get(`${API}/manuscripts/${manuscript.id}/outline`);
      setOutlineChapters(response.data.chapters || []);
    } catch (error) {
      notifyError('Failed to load outline');
      console.error(error);
      setShowOutlineEditor(false);
    } finally {
      setLoadingOutline(false);
    }
  };

  const handleAcceptOutlineWarning = () => {
    setShowOutlineWarning(false);
    loadOutlineForEditing(outlineEditorManuscript, isFirstTimeReview);
  };

  // Cancel ingestion - removes all files created during upload and returns to creator profile
  const handleCancelIngestion = async () => {
    if (!outlineEditorManuscript) return;
    
    setCancellingIngestion(true);
    try {
      // Use hard_delete=true to completely remove the manuscript and all associated files
      await axios.delete(`${API}/manuscripts/${outlineEditorManuscript.id}?hard_delete=true`);
      
      // UI updates - no toast needed (confirmation dialog + state reset shows success)
      
      // Close dialogs and reset state
      setShowCancelIngestionDialog(false);
      setShowOutlineEditor(false);
      setOutlineChapters([]);
      setOutlineEditorManuscript(null);
      setIsFirstTimeReview(false);
      
      // Refresh manuscripts list to remove the cancelled one
      loadManuscripts();
    } catch (error) {
      console.error('[Cancel Ingestion] Error:', error);
      notifyError(error.response?.data?.detail || 'Failed to cancel ingestion. Please try again.');
    } finally {
      setCancellingIngestion(false);
    }
  };

  const handleDeleteSection = async (sectionIndex) => {
    const targetChapter = outlineChapters[sectionIndex];
    
    // Confirm deletion
    if (!window.confirm(`Remove "${targetChapter.label}" from presentation order?\n\nThis will not delete the source text, only remove it from the audio book structure.`)) {
      return;
    }
    
    console.log(`[Delete Section] Removing section at index ${sectionIndex}:`, {
      label: targetChapter.label,
      title: targetChapter.title
    });
    
    // Remove section and reindex
    const updatedChapters = outlineChapters
      .filter((_, idx) => idx !== sectionIndex)
      .map((ch, idx) => ({ ...ch, index: idx }));
    
    console.log(`[Delete Section] Reindexed outline: ${updatedChapters.length} sections (was ${outlineChapters.length})`);
    
    // Update local state
    setOutlineChapters(updatedChapters);
    
    // Auto-save to backend
    setSavingOutline(true);
    try {
      await axios.patch(`${API}/manuscripts/${outlineEditorManuscript.id}/outline`, {
        chapters: updatedChapters
      });
      
      // Auto-saved silently - UI reflects change
      console.log('[Delete Section] Auto-saved to backend');
    } catch (error) {
      notifyError('Failed to save after deletion. Please try saving manually.');
      console.error('[Delete Section] Save error:', error);
    } finally {
      setSavingOutline(false);
    }
  };

  // Handle segment type change in outline editor
  const handleTypeChange = (sectionIndex, newType) => {
    const updatedChapters = outlineChapters.map((ch, idx) => 
      idx === sectionIndex ? { ...ch, type: newType } : ch
    );
    setOutlineChapters(updatedChapters);
    console.log(`[Type Change] Section ${sectionIndex} type changed to: ${newType}`);
  };

  const handleSaveOutline = async () => {
    if (!outlineEditorManuscript) return;
    
    setSavingOutline(true);
    try {
      // Step 1: Reindex and save outline
      const reindexed = outlineChapters.map((ch, idx) => ({ ...ch, index: idx }));
      
      await axios.patch(`${API}/manuscripts/${outlineEditorManuscript.id}/outline`, {
        chapters: reindexed
      });
      
      console.log('[Save Outline] Outline saved');
      
      // If this is first-time review (pre-TTS), don't regenerate, just proceed to TTS confirmation
      if (isFirstTimeReview) {
        // Progress modal shows next step - no toast needed
        setShowOutlineEditor(false);
        
        // Show TTS confirmation modal
        setUploadProgress(prev => ({
          ...prev,
          progress: {
            stage: 'awaiting_confirmation',
            percent: 70,
            message: 'Are you ready to convert to audio?',
            awaitingConfirmation: true
          },
          costEstimate: pendingManuscript?.estimated_cost
        }));
        
        setIsFirstTimeReview(false);
        return;
      }
      
      // For existing manuscripts, always close editor and refresh
      // Backend handles remapping vs regeneration automatically
      console.log('[Save Outline] Closing editor and refreshing manuscript list');
      
      setShowOutlineEditor(false);
      setOutlineChapters([]); // Clear cached outline
      
      // Check if TTS regeneration was triggered
      const manuscriptCheck = await axios.get(`${API}/manuscripts/${outlineEditorManuscript.id}`);
      const status = manuscriptCheck.data.tts_generation_status;
      
      // Silently log - UI shows manuscript status
      if (status === 'processing') {
        console.log('[Save Outline] Audio regeneration started');
      }
      
      // Navigate back to Creator Console if we came from there
      const returnTo = location.state?.returnTo;
      if (returnTo === '/author') {
        navigate('/author-console');
      } else {
        // Force refresh manuscript list to pick up any changes
        await loadManuscripts();
      }
      
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 400 && typeof detail === 'string' && detail.includes('already generating')) {
        notifyWarning('Outline saved. Audio generation already in progress.');
      } else if (detail) {
        // Handle Pydantic validation errors which may be arrays of objects
        let errorMsg = 'Error';
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail) && detail.length > 0) {
          errorMsg = detail[0]?.msg || detail[0]?.message || 'Validation error';
        } else if (detail?.msg) {
          errorMsg = detail.msg;
        }
        notifyError(`Error: ${errorMsg}`);
      } else {
        notifyError('Failed to save outline or regenerate audio');
      }
      console.error('[Save Outline] Error:', error);
    } finally {
      setSavingOutline(false);
    }
  };

  const handleRefineSection = async (sectionIndex) => {
    const targetChapter = outlineChapters[sectionIndex];
    console.log(`[Refine Section] Analyzing section at array index ${sectionIndex}:`, {
      label: targetChapter?.label,
      title: targetChapter?.title,
      charRange: `${targetChapter?.start_char_index}-${targetChapter?.end_char_index}`
    });
    
    setLoadingSplits(true);
    setRefineSectionIndex(sectionIndex);
    
    try {
      const response = await axios.post(
        `${API}/manuscripts/${outlineEditorManuscript.id}/suggest-splits`,
        { section_index: sectionIndex }
      );
      
      const data = response.data;
      console.log(`[Refine Section] Backend returned:`, {
        original_label: data.original_label,
        original_title: data.original_title,
        proposed_count: data.proposed_sections.length
      });
      
      setOriginalSectionInfo({
        label: data.original_label,
        title: data.original_title,
        start: data.original_start,
        end: data.original_end
      });
      
      if (data.proposed_sections.length === 0) {
        notifyInfo('No chapter headings detected in this section');
        setLoadingSplits(false);
        return;
      }
      
      setProposedSplits(
        data.proposed_sections.map((s, idx) => ({
          ...s,
          include: true,
          tempId: idx
        }))
      );
      setShowRefineSplits(true);
    } catch (error) {
      notifyError('Failed to analyze section');
      console.error('[Refine Section] Error:', error);
    } finally {
      setLoadingSplits(false);
    }
  };

  const handleApplySplits = async () => {
    if (!proposedSplits.length || refineSectionIndex === null) {
      setShowRefineSplits(false);
      return;
    }
    
    // Build new outline with splits applied
    const before = outlineChapters.slice(0, refineSectionIndex);
    const after = outlineChapters.slice(refineSectionIndex + 1);
    
    const newSections = proposedSplits
      .filter(s => s.include)
      .map(s => ({
        label: s.label,
        title: s.title || '',
        start_char_index: s.start_char_index,
        end_char_index: s.end_char_index,
        type: 'chapter',
        include_in_playback: true
      }));
    
    const merged = [...before, ...newSections, ...after];
    const reindexed = merged.map((ch, idx) => ({ ...ch, index: idx }));
    
    console.log(`[Apply Splits] Reindexed outline: ${reindexed.length} sections (was ${outlineChapters.length})`);
    
    // Update local state first
    setOutlineChapters(reindexed);
    setShowRefineSplits(false);
    
    // Save to backend immediately to keep in sync
    setSavingOutline(true);
    try {
      await axios.patch(`${API}/manuscripts/${outlineEditorManuscript.id}/outline`, {
        chapters: reindexed
      });
      
      // Silently saved - UI shows updated outline
      console.log('[Apply Splits] Auto-saved to backend');
    } catch (error) {
      notifyError('Failed to save splits. Please try saving manually.');
      console.error('[Apply Splits] Save error:', error);
    } finally {
      setSavingOutline(false);
      setProposedSplits([]);
      setRefineSectionIndex(null);
    }
  };

  // Helper: Detect if a section is a parent (like "Part I", "Book II", etc.)
  const isParentSection = (chapter) => {
    if (!chapter.label) return false;
    const label = chapter.label.toLowerCase();
    return (
      label.includes('part ') ||
      label.includes('book ') ||
      label.includes('section ') ||
      chapter.type === 'front_matter' ||
      chapter.type === 'back_matter'
    );
  };

  // Helper: Find children of a parent section
  const findSectionChildren = (chapters, parentIndex) => {
    const children = [];
    const parent = chapters[parentIndex];
    
    if (!isParentSection(parent)) return children;
    
    // Look for consecutive chapters that appear to belong to this section
    for (let i = parentIndex + 1; i < chapters.length; i++) {
      const chapter = chapters[i];
      
      // Stop if we hit another parent section
      if (isParentSection(chapter)) break;
      
      // If it's a regular chapter, consider it a child
      if (chapter.type === 'chapter' || chapter.label.toLowerCase().includes('chapter')) {
        children.push(i);
      } else {
        // Stop if we hit something else
        break;
      }
    }
    
    return children;
  };

  // Helper: Validate chapter ordering and warn about unusual patterns
  const validateChapterOrdering = (chapters) => {
    const warnings = [];
    
    // Check for out-of-order chapter numbering
    const chapterNumbers = [];
    chapters.forEach((ch, idx) => {
      const match = ch.label.match(/chapter\s+(\d+)/i);
      if (match) {
        chapterNumbers.push({ index: idx, number: parseInt(match[1]), label: ch.label });
      }
    });
    
    for (let i = 1; i < chapterNumbers.length; i++) {
      if (chapterNumbers[i].number < chapterNumbers[i - 1].number) {
        warnings.push(
          `⚠️ "${chapterNumbers[i].label}" appears before "${chapterNumbers[i - 1].label}"`
        );
      }
    }
    
    // Check for unusual section positions (like Epilogue in the middle)
    chapters.forEach((ch, idx) => {
      const label = ch.label.toLowerCase();
      if (label.includes('epilogue') || label.includes('afterword')) {
        if (idx < chapters.length - 3) { // Not near the end
          warnings.push(`⚠️ "${ch.label}" appears unusually early in the structure`);
        }
      }
      if (label.includes('prologue') || label.includes('foreword') || label.includes('preface')) {
        if (idx > 3) { // Not near the beginning
          warnings.push(`⚠️ "${ch.label}" appears unusually late in the structure`);
        }
      }
    });
    
    return warnings;
  };

  // Handle drag-and-drop reordering
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    const oldIndex = outlineChapters.findIndex(ch => (ch.id || `chapter-${outlineChapters.indexOf(ch)}`) === active.id);
    const newIndex = outlineChapters.findIndex(ch => (ch.id || `chapter-${outlineChapters.indexOf(ch)}`) === over.id);
    
    if (oldIndex === -1 || newIndex === -1) return;
    
    console.log(`[DRAG] Moving item from index ${oldIndex} to ${newIndex}`);
    
    const draggedChapter = outlineChapters[oldIndex];
    let reordered = [...outlineChapters];
    
    // Check if dragged item is a parent section with children
    if (isParentSection(draggedChapter)) {
      const childIndices = findSectionChildren(outlineChapters, oldIndex);
      
      if (childIndices.length > 0) {
        console.log(`[DRAG] Moving parent section "${draggedChapter.label}" with ${childIndices.length} children`);
        
        // Extract parent and all children
        const itemsToMove = [draggedChapter, ...childIndices.map(i => outlineChapters[i])];
        
        // Remove them from the array
        const indicesToRemove = [oldIndex, ...childIndices].sort((a, b) => b - a);
        indicesToRemove.forEach(i => {
          reordered.splice(i, 1);
        });
        
        // Insert at new position
        let insertIndex = newIndex;
        if (newIndex > oldIndex) {
          insertIndex -= (indicesToRemove.length - 1);
        }
        reordered.splice(insertIndex, 0, ...itemsToMove);
        
        // Silent log - visual reorder is clear to user
        console.log(`[DRAG] Moved "${draggedChapter.label}" with ${childIndices.length} associated chapters`);
      } else {
        // No children, simple move
        reordered = arrayMove(outlineChapters, oldIndex, newIndex);
      }
    } else {
      // Simple item move
      reordered = arrayMove(outlineChapters, oldIndex, newIndex);
    }
    
    // Validate and warn about unusual orderings
    const warnings = validateChapterOrdering(reordered);
    if (warnings.length > 0) {
      const warningText = warnings.join('\n');
      notifyWarning(`Unusual chapter ordering detected:\n${warningText}`);
    }
    
    // Reindex chapters
    const reindexed = reordered.map((ch, idx) => ({ ...ch, index: idx }));
    setOutlineChapters(reindexed);
    
    // Auto-save to backend (WITHOUT triggering TTS regeneration)
    setSavingOutline(true);
    try {
      await axios.patch(`${API}/manuscripts/${outlineEditorManuscript.id}/outline`, {
        chapters: reindexed,
        skip_tts_regeneration: true // Important: don't trigger TTS for reordering
      });
      
      // Silent save - visual reorder already confirms action
      console.log('[DRAG] Auto-saved new order to backend');
    } catch (error) {
      notifyError('Failed to save new order. Please try saving manually.');
      console.error('[DRAG] Save error:', error);
    } finally {
      setSavingOutline(false);
    }
  };

  const handleUpdateMetadata = async () => {
    if (!editingManuscript || !title || !author) {
      notifyError('Please fill in required fields (Title and Author)');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    
    if (coverImage) formData.append('cover_image', coverImage);
    formData.append('title', title);
    formData.append('subtitle', subtitle || '');
    formData.append('author', author);
    formData.append('publisher', publisher || '');
    formData.append('genre', genre || '');
    formData.append('theme', theme || '');
    formData.append('purchase_url', purchaseUrl || '');

    try {
      await axios.patch(`${API}/manuscripts/${editingManuscript.id}/metadata`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Form closes and list reloads - no toast needed
      
      // Reset form
      setFile(null);
      setCoverImage(null);
      setCoverPreview(null);
      setTitle('');
      setSubtitle('');
      setAuthor('');
      setPublisher('');
      setGenre(undefined);
      setTheme(undefined);
      setPurchaseUrl('');
      setShowEdit(false);
      setEditingManuscript(null);
      
      // Reload manuscripts
      await loadManuscripts();
    } catch (error) {
      console.error('Update error:', error);
      const detail = error.response?.data?.detail;
      let errorMsg = 'Failed to update manuscript';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMsg = detail[0]?.msg || detail[0]?.message || 'Validation error';
      } else if (detail?.msg) {
        errorMsg = detail.msg;
      }
      notifyError(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileExt = selectedFile.name.split('.').pop().toLowerCase();
      
      // NEW: EPUB-only validation
      if (fileExt !== 'epub') {
        notifyError('NewAudia currently only accepts EPUB files. Please export your manuscript as EPUB and upload that file.');
        return;
      }
      
      if (selectedFile.size > 20 * 1024 * 1024) {
        notifyError('File too large. Maximum size is 20 MB.');
        return;
      }
      
      setFile(selectedFile);
    }
  };

  const handleCoverImageSelect = (e) => {
    const selectedImage = e.target.files[0];
    if (selectedImage) {
      const fileExt = selectedImage.name.split('.').pop().toLowerCase();
      const allowedExts = ['jpg', 'jpeg', 'png', 'webp'];
      
      if (!allowedExts.includes(fileExt)) {
        notifyError('Unsupported image format. Please upload .jpg, .png, or .webp files.');
        return;
      }
      
      if (selectedImage.size > 5 * 1024 * 1024) {
        notifyError('Image too large. Maximum size is 5 MB.');
        return;
      }
      
      setCoverImage(selectedImage);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverPreview(reader.result);
      };
      reader.readAsDataURL(selectedImage);
    }
  };

  const handleUpload = async () => {
    if (!file || !title || !author) {
      notifyError('Please fill in all required fields (Title, Author, and select a file)');
      return;
    }

    setUploading(true);
    
    // Close the upload form immediately
    setShowUpload(false);
    
    // Show progress modal with initial metadata
    setUploadProgress({
      metadata: { 
        title, 
        author, 
        subtitle, 
        publisher,
        fileName: file.name,
        fileSize: (file.size / 1024).toFixed(1) + ' KB'
      },
      progress: { stage: 'uploading', percent: 10, message: 'Uploading manuscript...' }
    });

    const formData = new FormData();
    formData.append('file', file);
    if (coverImage) formData.append('cover_image', coverImage);
    formData.append('title', title);
    if (subtitle) formData.append('subtitle', subtitle);
    formData.append('author', author);
    if (publisher) formData.append('publisher', publisher);
    if (genre) formData.append('genre', genre);
    if (theme) formData.append('theme', theme);
    if (purchaseUrl) formData.append('purchase_url', purchaseUrl);

    try {
      // Stage 1: Uploading
      setUploadProgress(prev => ({
        ...prev,
        progress: { stage: 'uploading', percent: 20, message: 'Uploading manuscript...' }
      }));

      const response = await axios.post(`${API}/manuscripts/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const { session_id, manuscript_id, word_count, character_count, estimated_cost, chapter_count } = response.data;
      
      // Stage 2: Processing Complete - Show Results
      setUploadProgress(prev => ({
        ...prev,
        metadata: {
          ...prev.metadata,
          wordCount: word_count?.toLocaleString() || 'Calculating...',
          characterCount: character_count?.toLocaleString() || 'Calculating...',
          chapterCount: chapter_count || 0
        },
        progress: { 
          stage: 'processing', 
          percent: 40, 
          message: `Analysis complete: ${chapter_count || 0} ${chapter_count === 1 ? 'chapter' : 'chapters'} detected` 
        }
      }));
      
      await new Promise(resolve => setTimeout(resolve, 500)); // Brief pause to show processing
      
      // Stage 3: Prompt for Outline Review
      setUploadProgress(prev => ({
        ...prev,
        progress: { 
          stage: 'outline_review', 
          percent: 60, 
          message: 'Ready for outline review',
          awaitingOutlineReview: true
        }
      }));
      
      // Store manuscript info for later
      setPendingManuscript({ manuscript_id, session_id, estimated_cost });
      setUploading(false); // Allow user interaction
      
    } catch (error) {
      console.error('Upload error:', error);
      const detail = error.response?.data?.detail;
      let errorMsg = 'Failed to upload manuscript';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMsg = detail[0]?.msg || detail[0]?.message || 'Validation error';
      } else if (detail?.msg) {
        errorMsg = detail.msg;
      }
      notifyError(errorMsg);
      setUploadProgress(null);
    } finally {
      setUploading(false);
    }
  };

  const handleReviewOutline = async () => {
    if (!pendingManuscript) return;
    
    const { manuscript_id } = pendingManuscript;
    
    // Close upload progress modal
    setUploadProgress(null);
    
    // Load manuscript and open outline editor
    try {
      const manuscripts = await axios.get(`${API}/manuscripts`);
      const manuscript = manuscripts.data.find(m => m.id === manuscript_id);
      
      if (manuscript) {
        await openOutlineEditor(manuscript, null, true); // true = first time review
      }
    } catch (error) {
      notifyError('Failed to load manuscript for review');
      console.error(error);
    }
  };

  const handleSkipOutlineReview = () => {
    // Close upload progress and go directly to TTS confirmation
    setUploadProgress(prev => ({
      ...prev,
      progress: {
        stage: 'awaiting_confirmation',
        percent: 70,
        message: 'Are you ready to convert to audio?',
        awaitingConfirmation: true
      },
      costEstimate: pendingManuscript?.estimated_cost
    }));
  };

  const handleConfirmTTS = async () => {
    if (!pendingManuscript) return;
    
    const { manuscript_id, session_id } = pendingManuscript;
    setUploading(true);
    
    // Update progress to show TTS generation starting
    setUploadProgress(prev => ({
      ...prev,
      progress: { stage: 'generating', percent: 75, message: 'Generating audio (this may take several minutes)...' }
    }));
    
    try {
      // Call the backend to START TTS generation
      await axios.post(`${API}/manuscripts/${manuscript_id}/generate-audio`);
      
      // Now poll for completion
      await pollTTSProgress(manuscript_id, session_id);
    } catch (error) {
      console.error('TTS generation error:', error);
      notifyError('Failed to generate audio');
      setUploading(false);
      setUploadProgress(null);
      setPendingManuscript(null);
    }
  };
  
  const handleCancelTTS = () => {
    // Cancel the upload - visual feedback sufficient
    setUploadProgress(null);
    setPendingManuscript(null);
    setUploading(false);
  };

  const pollTTSProgress = async (manuscriptId, sessionId) => {
    const maxAttempts = 300; // 5 minutes max (polling every second)
    let attempts = 0;
    
    const poll = async () => {
      try {
        const response = await axios.get(`${API}/manuscripts/${manuscriptId}`);
        const status = response.data.tts_generation_status;
        const progressJson = response.data.tts_progress_json;
        
        // Parse progress data if available
        let progressData = null;
        if (progressJson) {
          try {
            progressData = JSON.parse(progressJson);
          } catch (e) {
            console.error('Failed to parse progress JSON:', e);
          }
        }
        
        // Update progress based on status
        if (status === 'processing') {
          let percent = 75;
          let message = 'Generating audio...';
          
          // Use real progress data if available
          if (progressData && progressData.total > 0) {
            percent = 70 + (progressData.percent * 0.25); // Map 0-100% to 70-95%
            const segmentLabel = progressData.completed === progressData.total 
              ? 'Finalizing...' 
              : `Segment ${progressData.current_chunk} of ${progressData.total}`;
            message = `Generating audio: ${segmentLabel} (${Math.round(progressData.percent)}%)`;
          } else {
            // Fallback to time-based progress
            percent = Math.min(70 + (attempts * 0.3), 90);
            message = 'Generating audio... This may take a few minutes.';
          }
          
          setUploadProgress(prev => ({
            ...prev,
            progress: {
              stage: 'generating',
              percent: Math.round(percent),
              message: message
            }
          }));
          
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, 2000); // Poll every 2 seconds
          } else {
            notifyError('Audio generation is taking longer than expected. Please check back later.');
            setUploadProgress(null);
            setPendingManuscript(null);
            setUploading(false);
            await loadManuscripts();
          }
        } else if (status === 'completed') {
          setUploadProgress(prev => ({
            ...prev,
            progress: { stage: 'complete', percent: 100, message: 'Audio ready!' }
          }));
          
          // Navigation happens - no toast needed
          
          // Wait a moment to show completion
          setTimeout(async () => {
            setUploadProgress(null);
            setPendingManuscript(null);
            
            // Reset form
            setFile(null);
            setCoverImage(null);
            setCoverPreview(null);
            setTitle('');
            setSubtitle('');
            setAuthor('');
            setPublisher('');
            setGenre(undefined);
            setTheme(undefined);
            setPurchaseUrl('');
            setShowUpload(false);
            setUploading(false);
            
            // Reload manuscripts
            await loadManuscripts();
            
            // Navigate back to Creator Console if we came from there, otherwise to player
            const returnTo = location.state?.returnTo;
            if (returnTo === '/author') {
              navigate('/author-console');
            } else {
              navigate(`/player/${sessionId}`);
            }
          }, 1500);
        } else if (status === 'failed') {
          notifyError('Audio generation failed. Please try again.');
          setUploadProgress(null);
          setPendingManuscript(null);
          setUploading(false);
          await loadManuscripts();
        }
      } catch (error) {
        console.error('Polling error:', error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          notifyError('Lost connection. Please refresh the page.');
          setUploadProgress(null);
          setPendingManuscript(null);
          setUploading(false);
        }
      }
    };
    
    // Start polling immediately
    poll();
  };

  const handleOpenManuscript = async (manuscriptId) => {
    try {
      const response = await axios.post(`${API}/manuscripts/${manuscriptId}/open`);
      const { session_id, resumed, position } = response.data;
      
      // Navigation happens - no toast needed
      if (resumed) {
        console.log(`[Manuscript] Resuming from word ${position}`);
      }
      
      navigate(`/player/${session_id}`);
    } catch (error) {
      console.error('Error opening manuscript:', error);
      notifyError('Failed to open manuscript');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (showUpload || showEdit) {
    const isEdit = showEdit;
    return (
      <div className="min-h-screen flex items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-2xl">
          {/* Header with Logo */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <img 
                src="/newaudia-logo.jpg" 
                alt="NewAudia" 
                className="h-20 sm:h-24 w-auto object-contain"
              />
            </div>
            <p className="text-base sm:text-lg text-gray-600 font-medium">
              {isEdit ? 'Edit Manuscript Metadata' : 'Upload New Manuscript'}
            </p>
          </div>

          <Card className="shadow-2xl bg-white/90 backdrop-blur-sm border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl text-gray-800">
                {isEdit ? <Edit className="w-6 h-6 text-blue-600" /> : <Upload className="w-6 h-6 text-teal-600" />}
                {isEdit ? 'Edit Metadata' : 'Manuscript Details'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Metadata Fields */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="title" className="text-base font-semibold text-gray-700">
                    Title <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="title"
                    data-testid="title-input"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter book title"
                    className="mt-1.5 text-base"
                  />
                </div>

                <div>
                  <Label htmlFor="subtitle" className="text-base font-semibold text-gray-700">
                    Subtitle
                  </Label>
                  <Input
                    id="subtitle"
                    data-testid="subtitle-input"
                    value={subtitle}
                    onChange={(e) => setSubtitle(e.target.value)}
                    placeholder="Enter subtitle (optional)"
                    className="mt-1.5 text-base"
                  />
                </div>

                <div>
                  <Label htmlFor="author" className="text-base font-semibold text-gray-700">
                    Author <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="author"
                    data-testid="author-input"
                    value={author}
                    onChange={(e) => setAuthor(e.target.value)}
                    placeholder="Enter author name"
                    className="mt-1.5 text-base"
                  />
                </div>

                <div>
                  <Label htmlFor="publisher" className="text-base font-semibold text-gray-700">
                    Publisher
                  </Label>
                  <Input
                    id="publisher"
                    data-testid="publisher-input"
                    value={publisher}
                    onChange={(e) => setPublisher(e.target.value)}
                    placeholder="Enter publisher name (optional)"
                    className="mt-1.5 text-base"
                  />
                </div>

                <div>
                  <Label className="text-base font-semibold text-gray-700">
                    Genre
                  </Label>
                  <Select value={genre || ''} onValueChange={(val) => {
                    setGenre(val || undefined);
                    setTheme(undefined); // Reset theme when genre changes
                  }}>
                    <SelectTrigger className="mt-1.5 text-base" data-testid="genre-select">
                      <SelectValue placeholder="Select genre (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {GENRE_OPTIONS.filter(opt => opt.value !== '').map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="theme" className="text-base font-semibold text-gray-700">
                    Theme
                  </Label>
                  <select
                    id="theme"
                    value={theme || ''}
                    onChange={(e) => setTheme(e.target.value || undefined)}
                    className="mt-1.5 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base shadow-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
                    data-testid="theme-select"
                  >
                    {getThemesForGenre(genre || '').map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="purchase-url" className="text-base font-semibold text-gray-700">
                    Purchase URL
                  </Label>
                  <Input
                    id="purchase-url"
                    data-testid="purchase-url-input"
                    value={purchaseUrl}
                    onChange={(e) => setPurchaseUrl(e.target.value)}
                    placeholder="https://... (link to buy paperback/hardcover)"
                    className="mt-1.5 text-base"
                  />
                </div>
              </div>

              {/* Cover Image Upload */}
              <div className="space-y-2">
                <Label className="text-base font-semibold text-gray-700">
                  Cover Image (Optional)
                </Label>
                <div className="flex gap-4">
                  <div
                    className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-teal-500 transition-colors cursor-pointer bg-gray-50 flex-1"
                    onClick={() => document.getElementById('cover-input').click()}
                  >
                    {coverPreview ? (
                      <div className="space-y-2">
                        <img
                          src={coverPreview}
                          alt="Cover preview"
                          className="w-32 h-48 object-cover mx-auto rounded"
                        />
                        <p className="text-sm text-gray-600">{coverImage.name}</p>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-10 h-10 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm font-medium text-gray-700 mb-1">
                          Click to upload cover
                        </p>
                        <p className="text-xs text-gray-500">
                          .jpg, .png, .webp (max 5 MB)
                        </p>
                      </>
                    )}
                    <input
                      id="cover-input"
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleCoverImageSelect}
                      className="hidden"
                      data-testid="cover-input"
                    />
                  </div>
                </div>
              </div>

              {/* File Upload - Only for new uploads */}
              {!isEdit && (
                <div className="space-y-2">
                  <Label className="text-base font-semibold text-gray-700">
                    Manuscript File <span className="text-red-500">*</span>
                  </Label>
                  <div
                    data-testid="upload-area"
                    className="border-2 border-dashed border-teal-300 rounded-lg p-6 text-center hover:border-teal-500 transition-colors cursor-pointer bg-teal-50/50"
                    onClick={() => document.getElementById('file-input').click()}
                  >
                    <FileText className="w-12 h-12 mx-auto mb-3 text-teal-600" />
                    <p className="text-base font-medium text-gray-700 mb-1">
                      {file ? file.name : 'Click to select EPUB file'}
                    </p>
                    <p className="text-sm text-gray-500">
                      EPUB only (.epub, max 20 MB)
                    </p>
                    <input
                      id="file-input"
                      type="file"
                      accept=".epub,application/epub+zip"
                      onChange={handleFileSelect}
                      className="hidden"
                      data-testid="file-input"
                    />
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    if (isEdit) {
                      setShowEdit(false);
                      setEditingManuscript(null);
                    } else {
                      setShowUpload(false);
                      // Navigate back to Creator Console if we came from there
                      const returnTo = location.state?.returnTo;
                      if (returnTo === '/author') {
                        navigate('/author-console');
                      }
                    }
                  }}
                  variant="outline"
                  className="flex-1 py-6 text-base"
                  data-testid="cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  onClick={isEdit ? handleUpdateMetadata : handleUpload}
                  disabled={uploading || (!isEdit && !file) || !title || !author}
                  className="flex-1 bg-teal-600 hover:bg-teal-700 text-white py-6 text-base font-semibold shadow-lg"
                  data-testid={isEdit ? "update-button" : "upload-button"}
                >
                  {uploading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {isEdit ? 'Updating...' : 'Processing...'}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      {isEdit ? <Edit className="w-5 h-5" /> : <Upload className="w-5 h-5" />}
                      {isEdit ? 'Update Metadata' : 'Upload & Begin'}
                    </span>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 sm:p-8">
      {/* Upload Progress Modal */}
      {uploadProgress && (
        <UploadProgress
          metadata={uploadProgress.metadata}
          progress={uploadProgress.progress}
          costEstimate={uploadProgress.costEstimate}
          onConfirm={handleConfirmTTS}
          onCancel={handleCancelTTS}
          onReviewOutline={handleReviewOutline}
          onSkipReview={handleSkipOutlineReview}
        />
      )}
      
      {/* Hide main content when outline editor is open */}
      {!showOutlineEditor && (
        <div className="max-w-6xl mx-auto">
          {/* Header with Logo */}
          <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img 
              src="/newaudia-logo.jpg" 
              alt="NewAudia" 
              className="h-20 sm:h-24 w-auto object-contain"
            />
          </div>
          <p className="text-base sm:text-lg text-gray-600 font-medium">
            Your Intelligent Audiobook Library
          </p>
        </div>

        {/* Upload Button and Archive Toggle */}
        <div className="mb-8 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <Button
            onClick={() => setShowUpload(true)}
            className="bg-teal-600 hover:bg-teal-700 text-white py-6 px-8 text-lg font-semibold shadow-lg"
            data-testid="new-upload-button"
          >
            <Upload className="w-5 h-5 mr-2" />
            Upload New Manuscript
          </Button>
          
          <Button
            onClick={() => {
              setShowArchived(!showArchived);
              setLoading(true);
              loadManuscripts();
            }}
            variant="outline"
            className="py-6 px-6 text-base"
            data-testid="toggle-archived-button"
          >
            <Archive className="w-4 h-4 mr-2" />
            {showArchived ? 'Hide Archived' : 'Show Archived'}
          </Button>
        </div>

        {/* Manuscripts Library */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-12 h-12 animate-spin text-teal-600" />
          </div>
        ) : manuscripts.length === 0 ? (
          <Card className="shadow-xl bg-white/90 backdrop-blur-sm border-0 text-center py-16">
            <BookOpen className="w-20 h-20 mx-auto mb-4 text-gray-400" />
            <p className="text-xl text-gray-600 mb-2">Your library is empty</p>
            <p className="text-sm text-gray-500">Upload your first manuscript to get started</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {manuscripts.map((manuscript) => (
              <Card
                key={manuscript.id}
                className="shadow-2xl bg-white border-0 hover:shadow-3xl hover:scale-[1.02] transition-all duration-300 cursor-pointer relative"
                data-testid={`manuscript-card-${manuscript.id}`}
              >
                <div 
                  onClick={() => {
                    // Only allow opening if TTS is completed
                    if (manuscript.tts_generation_status === 'completed') {
                      handleOpenManuscript(manuscript.id);
                    }
                    // Visual styling (opacity-60 cursor-not-allowed) already shows disabled state
                  }}
                  className={manuscript.tts_generation_status !== 'completed' ? 'opacity-60 cursor-not-allowed' : ''}
                >
                  {/* Cover Image - Larger and more prominent with cache-busting */}
                  <div className="relative w-full h-56 bg-gradient-to-br from-slate-900 via-teal-900 to-cyan-900 overflow-hidden flex items-center justify-center">
                    {/* Decorative gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent z-10"></div>
                    
                    <img
                      src={manuscript.cover_image_path 
                        ? `${API}/covers/${manuscript.id}?v=${manuscript.cover_updated_at || manuscript.updated_at || Date.now()}` 
                        : '/newaudia-logo.jpg'}
                      alt={`${manuscript.title} cover`}
                      className="relative z-0 max-w-full max-h-full object-contain p-4 drop-shadow-2xl"
                      onError={(e) => {
                        e.target.src = '/newaudia-logo.jpg';
                      }}
                    />
                    
                    {/* Status badge overlay on image */}
                    {(() => {
                      const status = manuscript.status || 'new';
                      if (status === 'new') {
                        return (
                          <div className="absolute top-3 left-3 z-20 bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                            NEW
                          </div>
                        );
                      } else if (status === 'archived') {
                        return (
                          <div className="absolute top-3 left-3 z-20 bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                            ARCHIVED
                          </div>
                        );
                      } else if (status === 'active') {
                        return (
                          <div className="absolute top-3 left-3 z-20 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                            ACTIVE
                          </div>
                        );
                      }
                    })()}
                  </div>
                  
                  <CardHeader className="bg-gradient-to-r from-slate-50 to-gray-50 border-b-2 border-teal-500 overflow-visible">
                    <div className="relative group overflow-visible">
                      <CardTitle className="text-2xl font-black text-gray-900 cursor-help leading-tight">
                        {manuscript.title}
                      </CardTitle>
                      {/* Summary Tooltip on Hover over Title */}
                      {manuscript.summary && (
                        <div className="absolute left-0 top-full mt-2 w-96 max-w-[calc(100vw-2rem)] bg-gray-900 text-white text-sm p-4 rounded-lg shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-[9999] max-h-64 overflow-y-auto pointer-events-none group-hover:pointer-events-auto">
                          <div className="font-semibold mb-2 text-teal-300">Summary</div>
                          <p className="leading-relaxed">{manuscript.summary}</p>
                          <div className="absolute left-4 -top-2 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-b-8 border-b-gray-900"></div>
                        </div>
                      )}
                      
                      {manuscript.subtitle && (
                        <CardDescription className="text-base text-gray-700 font-medium mt-2">
                          {manuscript.subtitle}
                        </CardDescription>
                      )}
                      
                      {/* Order Paperback Link - Under Subtitle */}
                      {manuscript.purchase_url && (
                        <div className="mt-3">
                          <a
                            href={manuscript.purchase_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-2 text-sm font-bold text-teal-600 hover:text-teal-700 hover:underline transition-colors"
                          >
                            <ShoppingCart className="w-4 h-4" />
                            Order Paperback Today
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  
                  <CardContent className="space-y-3 pt-4 pb-3">
                    {/* Author - Prominent */}
                    <div className="flex items-center gap-3 bg-teal-50 p-3 rounded-lg">
                      <User className="w-5 h-5 text-teal-700 flex-shrink-0" />
                      <span className="font-bold text-gray-900 text-base">{manuscript.author}</span>
                    </div>
                    
                    {/* Publisher */}
                    {manuscript.publisher && (
                      <div className="flex items-center gap-3 bg-slate-50 p-3 rounded-lg">
                        <Building2 className="w-5 h-5 text-slate-600 flex-shrink-0" />
                        <span className="font-semibold text-gray-800 text-sm">{manuscript.publisher}</span>
                      </div>
                    )}
                    
                    {/* Chapter Count - Enhanced */}
                    <div className="flex items-center justify-between bg-gradient-to-r from-teal-500 to-cyan-500 text-white p-3 rounded-lg shadow-md">
                      <div className="flex items-center gap-3">
                        <BookOpen className="w-5 h-5 flex-shrink-0" />
                        <span className="font-bold text-base">
                          {manuscript.chapter_count > 1 
                            ? `${manuscript.chapter_count} Chapters` 
                            : manuscript.chapter_count === 1 
                              ? '1 Chapter' 
                              : 'Processing...'}
                        </span>
                      </div>
                      
                      {manuscript.chapter_count > 0 && (
                        <Button
                          size="sm"
                          className="h-8 px-3 text-xs font-bold bg-white text-teal-600 hover:bg-teal-50 shadow-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOutlineManuscriptId(manuscript.id);
                            setShowOutlinePreview(true);
                          }}
                        >
                          <List className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      )}
                    </div>
                    
                    {/* Outline Editor Button */}
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full mt-2 h-9 px-3 hover:bg-purple-50 border-purple-300 hover:border-purple-500"
                      onClick={(e) => openOutlineEditor(manuscript, e)}
                      data-testid={`outline-button-${manuscript.id}`}
                    >
                      <List className="w-4 h-4 mr-2 text-purple-600" />
                      <span className="text-purple-600 font-semibold text-sm">Edit Outline</span>
                    </Button>
                    
                    {/* Engagement Stats */}
                    {(manuscript.total_words_consumed > 0 || manuscript.total_listen_seconds > 0) && (
                      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-3 space-y-1">
                        <div className="text-xs font-bold text-emerald-700 mb-2">📊 Engagement Stats</div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-gray-600">Words:</span>{' '}
                            <span className="font-semibold text-gray-900">{(manuscript.total_words_consumed || 0).toLocaleString()}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Time:</span>{' '}
                            <span className="font-semibold text-gray-900">
                              {(() => {
                                const total = Math.floor(manuscript.total_listen_seconds || 0);
                                const hours = Math.floor(total / 3600);
                                const minutes = Math.floor((total % 3600) / 60);
                                const secs = total % 60;
                                if (hours > 0) return `${hours}h ${minutes}m`;
                                return `${minutes}m ${secs}s`;
                              })()}
                            </span>
                          </div>
                        </div>
                        <div className="pt-1 border-t border-emerald-200 mt-2">
                          <span className="text-gray-600 text-xs">Reader Credits:</span>{' '}
                          <span className="font-bold text-emerald-700">{(manuscript.credits?.total ?? 0).toFixed(2)}</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Regeneration Badge */}
                    {manuscript.tts_needs_regeneration && (
                      <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-3">
                        <p className="text-sm text-yellow-900 font-semibold flex items-center gap-2">
                          <span className="text-lg">⚠️</span>
                          Structure changed – Regenerate Audio
                        </p>
                        <p className="text-xs text-yellow-800 mt-1">
                          Outline has been modified. Audio needs regeneration.
                        </p>
                      </div>
                    )}
                    
                    {/* Manuscript Status & Activation */}
                    {(() => {
                      const status = manuscript.status || 'new';
                      if (status === 'new' && manuscript.tts_generation_status === 'completed') {
                        return (
                          <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-3 space-y-2">
                            <p className="text-sm text-blue-900 font-semibold">
                              ⚠️ This manuscript is <span className="font-black">NOT YET ACTIVE</span>
                            </p>
                            <p className="text-xs text-blue-800">
                              Activate it to make it visible to readers and monetizable
                            </p>
                            <Button
                              className="w-full py-3 text-sm font-bold bg-blue-600 hover:bg-blue-700 text-white"
                              onClick={(e) => handleActivateManuscript(manuscript, e)}
                            >
                              Activate Now
                            </Button>
                          </div>
                        );
                      } else if (status === 'archived') {
                        return (
                          <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3">
                            <p className="text-sm text-amber-900 font-semibold">
                              📦 Archived - Not visible to readers
                            </p>
                          </div>
                        );
                      } else if (status === 'active') {
                        return (
                          <div className="bg-green-50 border-2 border-green-300 rounded-lg p-3">
                            <p className="text-sm text-green-900 font-semibold">
                              ✅ Active - Visible to readers & monetizable
                            </p>
                          </div>
                        );
                      }
                    })()}
                    
                    {/* READ NOW Button - Only show if audio is ready */}
                    {manuscript.tts_generation_status === 'completed' ? (
                      <Button
                        className="w-full py-6 text-lg font-bold bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-lg"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenManuscript(manuscript.id);
                        }}
                      >
                        <Play className="w-6 h-6 mr-2" />
                        READ NOW
                      </Button>
                    ) : manuscript.tts_generation_status === 'processing' && manuscript.tts_progress_json ? (
                      <div className="w-full text-center py-4 px-4 rounded-lg font-bold text-sm bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-md">
                        {(() => {
                          try {
                            const progress = JSON.parse(manuscript.tts_progress_json);
                            if (progress.total > 0) {
                              return `🎵 Generating: ${progress.completed}/${progress.total} (${Math.round(progress.percent || 0)}%)`;
                            }
                          } catch (e) {
                            // Ignore parse errors
                          }
                          return '⏳ GENERATING AUDIO...';
                        })()}
                      </div>
                    ) : (
                      <div className="w-full text-center py-4 px-4 rounded-lg font-bold text-sm bg-gradient-to-r from-gray-400 to-gray-500 text-white shadow-md">
                        {manuscript.tts_generation_status === 'failed' ? '❌ GENERATION FAILED' : '⏸️ AWAITING CONFIRMATION'}
                      </div>
                    )}
                    
                    {/* Action Buttons - Bottom of Card */}
                    <div className="flex justify-center gap-2 pt-3 border-t border-gray-200">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-10 px-3 hover:bg-blue-100 border-blue-300 hover:border-blue-500"
                        onClick={(e) => openEditDialog(manuscript, e)}
                        data-testid={`edit-button-${manuscript.id}`}
                      >
                        <Edit className="w-4 h-4 mr-1 text-blue-600" />
                        <span className="text-blue-600 font-semibold text-xs">Edit</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-10 px-3 hover:bg-amber-100 border-amber-300 hover:border-amber-500"
                        onClick={(e) => openArchiveDialog(manuscript, e)}
                        data-testid={`archive-button-${manuscript.id}`}
                      >
                        <Archive className={`w-4 h-4 mr-1 ${manuscript.status === 'archived' ? 'text-amber-600' : 'text-amber-500'}`} />
                        <span className={`font-semibold text-xs ${manuscript.status === 'archived' ? 'text-amber-600' : 'text-amber-500'}`}>
                          {manuscript.status === 'archived' ? "Unarchive" : "Archive"}
                        </span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-10 px-3 hover:bg-red-100 border-red-300 hover:border-red-500"
                        onClick={(e) => openDeleteDialog(manuscript, e)}
                        data-testid={`delete-button-${manuscript.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1 text-red-600" />
                        <span className="text-red-600 font-semibold text-xs">Delete</span>
                      </Button>
                    </div>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-600">
          <p>Acorn v1.0 - Intelligent audiobook experience</p>
        </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Manuscript</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedManuscript?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteManuscript}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive Confirmation Dialog */}
      <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {selectedManuscript?.status === 'archived' ? 'Unarchive Manuscript' : 'Archive Manuscript'}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              {selectedManuscript?.status === 'archived' ? (
                <>
                  <p>
                    Are you sure you want to <strong>unarchive</strong> "{selectedManuscript?.title}"?
                  </p>
                  <p className="text-sm">
                    This manuscript will be restored to active status, made visible to readers, 
                    and become monetizable again.
                  </p>
                </>
              ) : (
                <>
                  <p>
                    Are you sure you want to <strong>archive</strong> "{selectedManuscript?.title}"?
                  </p>
                  <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
                    <p className="text-sm font-semibold text-amber-900 mb-2">
                      ⚠️ Important Information:
                    </p>
                    <ul className="text-sm text-amber-800 space-y-1 list-disc list-inside">
                      <li>The manuscript will remain on file</li>
                      <li>It will <strong>not be visible</strong> to readers</li>
                      <li>It will <strong>not be monetizable</strong> until reactivated</li>
                      <li>You can unarchive it at any time to restore access</li>
                    </ul>
                  </div>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleArchiveManuscript}
              className={selectedManuscript?.status === 'archived' ? 'bg-green-600 hover:bg-green-700' : 'bg-amber-600 hover:bg-amber-700'}
            >
              {selectedManuscript?.status === 'archived' ? 'Unarchive' : 'Archive'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Outline Preview Modal */}
      {/* Outline Editor Modal */}
      {showOutlineEditor && outlineEditorManuscript && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="border-b border-gray-200 p-6">
              <h2 className="text-2xl font-bold text-gray-900">
                Edit Outline: {outlineEditorManuscript.title}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Refine sections to create meaningful chapter-level audio units
              </p>
              <div className="mt-3 bg-purple-50 border border-purple-200 rounded-lg p-3">
                <p className="text-xs text-purple-900 font-medium flex items-center gap-2">
                  <span>💡</span>
                  <span>Splits & deletions auto-save. Clicking "Save & Close" will regenerate audio to match your changes.</span>
                </p>
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingOutline ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                  <span className="ml-3 text-gray-600">Loading outline...</span>
                </div>
              ) : (
                <div className="space-y-3">
                  {outlineChapters.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>No sections in outline</p>
                    </div>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={outlineChapters.map((ch, idx) => ch.id || `chapter-${idx}`)}
                        strategy={verticalListSortingStrategy}
                      >
                        {outlineChapters.map((chapter, idx) => (
                          <SortableChapterItem
                            key={chapter.id || `chapter-${idx}`}
                            chapter={{ ...chapter, id: chapter.id || `chapter-${idx}` }}
                            index={idx}
                            onRefine={handleRefineSection}
                            onDelete={handleDeleteSection}
                            onTypeChange={handleTypeChange}
                            loadingSplits={loadingSplits}
                            refineSectionIndex={refineSectionIndex}
                            savingOutline={savingOutline}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  )}
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="border-t border-gray-200 p-6 flex justify-between">
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowOutlineEditor(false);
                    setOutlineChapters([]);
                    loadManuscripts(); // Refresh in case splits were auto-saved
                  }}
                >
                  Close
                </Button>
                
                {/* Cancel Ingestion button - only show during first-time review (before TTS) */}
                {(isFirstTimeReview || outlineEditorManuscript?.tts_generation_status === 'pending') && (
                  <Button
                    variant="destructive"
                    onClick={() => setShowCancelIngestionDialog(true)}
                    disabled={cancellingIngestion || savingOutline}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Cancel Upload
                  </Button>
                )}
              </div>
              <Button
                className="bg-purple-600 hover:bg-purple-700"
                onClick={handleSaveOutline}
                disabled={savingOutline || loadingOutline}
              >
                {savingOutline ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save & Close'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Refine Splits Modal */}
      {showRefineSplits && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="border-b border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900">
                Suggested Splits
              </h2>
              {originalSectionInfo && (
                <p className="text-sm text-gray-600 mt-1">
                  Original: {originalSectionInfo.label}
                  {originalSectionInfo.title && ` - ${originalSectionInfo.title}`}
                </p>
              )}
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-3">
                {proposedSplits.map((split, idx) => (
                  <div
                    key={split.tempId}
                    className={`border rounded-lg p-4 transition-all ${
                      split.include 
                        ? 'border-green-300 bg-green-50' 
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <input
                        type="checkbox"
                        checked={split.include}
                        onChange={(e) => {
                          setProposedSplits(prev =>
                            prev.map(s =>
                              s.tempId === split.tempId
                                ? { ...s, include: e.target.checked }
                                : s
                            )
                          );
                        }}
                        className="mt-1 w-5 h-5"
                      />
                      <div className="flex-1">
                        <Input
                          value={split.label}
                          onChange={(e) => {
                            setProposedSplits(prev =>
                              prev.map(s =>
                                s.tempId === split.tempId
                                  ? { ...s, label: e.target.value }
                                  : s
                              )
                            );
                          }}
                          className="font-bold mb-2"
                          placeholder="Chapter label"
                        />
                        <Input
                          value={split.title}
                          onChange={(e) => {
                            setProposedSplits(prev =>
                              prev.map(s =>
                                s.tempId === split.tempId
                                  ? { ...s, title: e.target.value }
                                  : s
                              )
                            );
                          }}
                          className="text-sm"
                          placeholder="Chapter title (optional)"
                        />
                        <div className="text-xs text-gray-400 mt-2">
                          {split.start_char_index} - {split.end_char_index} 
                          ({(split.end_char_index - split.start_char_index).toLocaleString()} chars)
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Footer */}
            <div className="border-t border-gray-200 p-6 flex justify-between">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRefineSplits(false);
                  setProposedSplits([]);
                  setRefineSectionIndex(null);
                }}
              >
                Cancel
              </Button>
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={handleApplySplits}
              >
                Apply Splits ({proposedSplits.filter(s => s.include).length} sections)
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Outline Warning Dialog */}
      {showOutlineWarning && (
        <div className="fixed inset-0 bg-black/70 z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                ⚠️ Important: Outline Modifications
              </h2>
              <div className="space-y-3 text-sm text-gray-700">
                <p className="font-semibold text-red-600">
                  Changes to the outline structure cannot be undone.
                </p>
                <p>
                  When you modify the outline (split sections, delete sections, reorder chapters), these changes directly affect:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>How the manuscript is divided into audio segments</li>
                  <li>Chapter navigation in the player</li>
                  <li>The structure of your published audiobook</li>
                </ul>
                <div className="bg-yellow-50 border border-yellow-300 rounded p-3">
                  <p className="text-xs text-yellow-900">
                    <strong>Note:</strong> Your original text is never modified. Only the presentation structure changes.
                  </p>
                </div>
                <p className="font-medium">
                  Do you understand and authorize these changes?
                </p>
              </div>
            </div>
            <div className="border-t border-gray-200 p-4 flex justify-between">
              <Button
                variant="outline"
                onClick={() => {
                  setShowOutlineWarning(false);
                  setOutlineEditorManuscript(null);
                }}
              >
                Cancel
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={handleAcceptOutlineWarning}
              >
                I Understand, Proceed
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Ingestion Confirmation Dialog */}
      {showCancelIngestionDialog && (
        <div className="fixed inset-0 bg-black/70 z-[80] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md">
            <div className="p-6">
              <h2 className="text-xl font-bold text-red-600 mb-3">
                🛑 Cancel This Upload?
              </h2>
              <div className="space-y-3 text-sm text-gray-700">
                <p>
                  Cancelling will <strong>permanently remove</strong> all files created during this upload:
                </p>
                <ul className="list-disc pl-5 space-y-1 text-gray-600">
                  <li>The uploaded manuscript file</li>
                  <li>The generated outline structure</li>
                  <li>Any processed content</li>
                </ul>
                <div className="bg-red-50 border border-red-200 rounded p-3 mt-4">
                  <p className="text-xs text-red-800">
                    <strong>This action cannot be undone.</strong> You will need to upload the manuscript again if you wish to proceed later.
                  </p>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 p-4 flex justify-between">
              <Button
                variant="outline"
                onClick={() => setShowCancelIngestionDialog(false)}
                disabled={cancellingIngestion}
              >
                Go Back
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={handleCancelIngestion}
                disabled={cancellingIngestion}
              >
                {cancellingIngestion ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Cancelling...
                  </>
                ) : (
                  'Yes, Cancel Upload'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showOutlinePreview && outlineManuscriptId && (
        <ManuscriptOutlinePreview
          manuscriptId={outlineManuscriptId}
          onClose={() => {
            setShowOutlinePreview(false);
            setOutlineManuscriptId(null);
          }}
        />
      )}
    </div>
  );
};

export default HomePage;
